import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryState {
  failed: boolean;
}

/**
 * Last-resort fallback for rendering crashes. Without it one unexpected
 * response shape unmounts the whole tree and leaves a blank page. The
 * fallback uses plain links and a full reload rather than router state,
 * since the router itself may be what failed.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Unhandled rendering error', error, info.componentStack);
  }

  render() {
    if (!this.state.failed) {
      return this.props.children;
    }
    return (
      <main className="centered-page">
        <h1>This page failed to load</h1>
        <p role="alert">
          Something unexpected went wrong while showing this page. Reloading usually fixes it; if it
          keeps happening, start again from the home page.
        </p>
        <div className="button-row">
          <button type="button" className="button" onClick={() => window.location.reload()}>
            Reload page
          </button>
          <a className="button button-quiet" href="/">
            Go to home page
          </a>
        </div>
      </main>
    );
  }
}
